import z from 'zod';

import { isValidCPF } from './cpf';
import { legalEntityRelationValidator, socialContractFileItemSchema, SKIPPABLE_DOCUMENT_STATUSES } from './registry';

const signerDocumentValidator = z
  .string()
  .min(1, 'O CPF é obrigatório')
  .refine(isValidCPF, { message: 'CPF inválido' });

const forceManualReviewField = {
  forceManualReview: z.boolean().optional()
};

export const cnhValidator = z.object({
  documentType: z.literal('cnh'),
  signerDocument: signerDocumentValidator,
  documentHash: z.string().min(1, 'O documento é obrigatório'),
  documentBackHash: z.string().optional(),
  selfieHash: z.string().min(1, 'Selfie hash is required'),
  documentStatus: z.enum(SKIPPABLE_DOCUMENT_STATUSES),
  legalEntityRelation: legalEntityRelationValidator.optional(),
  ...forceManualReviewField
});

export type CNHAnalyzerInput = z.infer<typeof cnhValidator>;

export const rgValidator = z.object({
  documentType: z.literal('rg'),
  signerDocument: signerDocumentValidator,
  documentFrontHash: z.string().min(1, 'O documento da frente é obrigatório'),
  documentBackHash: z.string().optional(),
  selfieHash: z.string().min(1, 'Selfie hash is required'),
  documentStatus: z.enum(SKIPPABLE_DOCUMENT_STATUSES),
  legalEntityRelation: legalEntityRelationValidator.optional(),
  ...forceManualReviewField
});

export type RGAnalyzerInput = z.infer<typeof rgValidator>;

export const cinValidator = z.object({
  documentType: z.literal('cin'),
  signerDocument: signerDocumentValidator,
  documentFrontHash: z.string().min(1, 'O documento da frente é obrigatório'),
  documentBackHash: z.string().optional(),
  selfieHash: z.string().min(1, 'Selfie hash is required'),
  documentStatus: z.enum(SKIPPABLE_DOCUMENT_STATUSES),
  legalEntityRelation: legalEntityRelationValidator.optional(),
  ...forceManualReviewField
});

export type CINAnalyzerInput = z.infer<typeof cinValidator>;

export const passportValidator = z.object({
  documentType: z.literal('passport'),
  signerDocument: signerDocumentValidator,
  documentHash: z.string().min(1, 'O documento é obrigatório'),
  selfieHash: z.string().min(1, 'Selfie hash is required'),
  documentStatus: z.enum(SKIPPABLE_DOCUMENT_STATUSES),
  legalEntityRelation: legalEntityRelationValidator.optional(),
  ...forceManualReviewField
});

export type PassportAnalyzerInput = z.infer<typeof passportValidator>;

export const documentAnalyzerInput = z.union([cnhValidator, rgValidator, cinValidator, passportValidator]);
export type DocumentAnalyzerInput = z.infer<typeof documentAnalyzerInput>;

export const analyzeDocumentValidator = z.discriminatedUnion('documentType', [
  cnhValidator,
  rgValidator,
  cinValidator,
  passportValidator
]);

export type AnalyzeDocumentInput = z.infer<typeof analyzeDocumentValidator>;

export const analyzeSocialContractValidator = z.object({
  socialContractFiles: z
    .array(socialContractFileItemSchema)
    .min(1, 'Pelo menos um arquivo de contrato social deve ser enviado')
});

export type AnalyzeSocialContractInput = z.infer<typeof analyzeSocialContractValidator>;

export const errorObjectValidator = z.object({
  code: z.string(),
  message: z.string(),
  details: z.string().optional()
});

export type ErrorObject = z.infer<typeof errorObjectValidator>;

export const documentAnalysisResult = z.object({
  errors: z.array(errorObjectValidator)
});
